import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authModule } from "../../api/modules/auth.module";
import { showToast } from "../../utils/toast";
import { setSession } from "../../auth/tokenStorage";
import { track, identifyUser } from "../../analytics/track";
import { EVENTS } from "../../analytics/events";
import { OtpView, EMPTY_CODE } from "../OtpVerification/otp.view";

/**
 * `/email-verification-otp`, the container. The sign-up email check.
 *
 * Shares `otp.view.jsx` with `/otp-verification`. What is genuinely this
 * route's own: it reads the address from `?email=` as well as route state
 * (Login and Signup both redirect here with a query string), it counts failed
 * attempts per address for analytics, and a verified user lands in the app
 * rather than in a password form.
 *
 * The page emits no class name from `OtpVerification.css` any more and no
 * longer reaches across directories to import it. That file is gone.
 */

const OTP_ATTEMPT_KEY_PREFIX = "vaultsa_2fa_attempts_";
const getOtpAttemptKey = (email) => `${OTP_ATTEMPT_KEY_PREFIX}${email || "unknown"}`;

const RESEND_COOLDOWN_SECONDS = 60;

export default function EmailVerificationOtp() {
  const [otp, setOtp] = useState(EMPTY_CODE);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [focusSignal, setFocusSignal] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  // Route state when we arrived by navigation, `?email=` when Login or Signup
  // redirected here.
  const queryParams = new URLSearchParams(location.search);
  const emailFromQuery = queryParams.get("email");
  const email = location.state?.email || emailFromQuery || "";

  const clearCode = useCallback(() => {
    setOtp(EMPTY_CODE);
    setFocusSignal((signal) => signal + 1);
  }, []);

  useEffect(() => {
    // Focus the first cell on mount.
    setFocusSignal((signal) => signal + 1);

    // Without an address there is nothing to verify against, so there is no
    // screen to show.
    if (!email) {
      showToast.error("Email address is required for verification");
      navigate("/sign-up");
    }
  }, [email, navigate]);

  // Resend cooldown.
  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const interval = setInterval(() => {
      setResendTimer((timer) => timer - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleChange = (next) => {
    setOtp(next);
    if (error) setError("");
  };

  const handleSubmit = async (otpCode) => {
    if (otpCode.length !== 6) {
      setError("Enter all six digits.");
      setError("Enter all 6 digits of the code.");
      return;
    }

    setLoading(true);

    try {
      const result = await authModule.verifyEmailOtp({
        email: email,
        otp: otpCode,
      });

      if (result.success) {
        // Backend sets HttpOnly auth cookies on a successful verify and
        // returns the user blob in `details`. We cache the user for UI
        // restore, cookies handle authentication transparently.
        const details = result.data?.details;
        if (details) {
          const userData = {
            id: details.id,
            email: details.email,
            full_name: details.full_name,
            role: details.role,
          };

          setSession({ user: userData });

          localStorage.removeItem(getOtpAttemptKey(email));
          identifyUser(userData);
          showToast.success("Email verified and logged in successfully!");
          setSuccess("Email verified. Taking you to the marketplace.");

          navigate("/");
        } else {
          showToast.success(result.message || "Email verified successfully!");
          setSuccess(result.message || "Email verified. Taking you to sign in.");

          navigate("/login", {
            state: {
              message: "Your email has been verified. You can now log in.",
            },
          });
        }
      } else {
        const attemptKey = getOtpAttemptKey(email);
        const attempts =
          (parseInt(localStorage.getItem(attemptKey) || "0", 10) || 0) + 1;
        localStorage.setItem(attemptKey, String(attempts));
        const rawMessage = result.message || "";
        const msg = rawMessage.toLowerCase();
        let failureReason = "unknown";
        if (msg.includes("invalid") || msg.includes("incorrect") || msg.includes("wrong"))
          failureReason = "invalid_otp";
        else if (msg.includes("expire")) failureReason = "expired";
        else if (msg.includes("rate") || msg.includes("too many") || msg.includes("max"))
          failureReason = "rate_limited";
        track(EVENTS.SIGNUP_2FA_FAILED, {
          method: "email",
          failure_reason: failureReason,
          failure_message: rawMessage.slice(0, 200),
          attempt_count: attempts,
        });
        /* ONE MESSAGE, NOT TWO. This fired a toast AND an inline error
           carrying the same string, so the screen said the same sentence in
           two places at once. The inline one wins: it sits with the cells that
           are wrong, it survives, and the field is already on screen — a toast
           is for something that happened somewhere the buyer is not looking.

           And it is our sentence, not the API's. `result.message` is
           "Invalid or expired OTP.", which names a variable and tells someone
           staring at six empty boxes nothing about what to do next. The raw
           text still goes to analytics above, where it belongs. */
        setError(
          "That code is not right. Check the latest email and try again, or send a new code."
        );
        clearCode();
      }
    } catch (err) {
      // Same rule as above: the inline message is the one that stays put.
      setError("Something went wrong. Please try again.");
      console.error("OTP verification error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;

    setResendLoading(true);

    try {
      const result = await authModule.resendVerification(email);

      if (result.success) {
        showToast.success("New verification code sent to your email");
        setError("");
        clearCode();
        setResendTimer(RESEND_COOLDOWN_SECONDS);
      } else {
        showToast.error(result.message || "Failed to resend verification code");
        setError(result.message || "We could not send a new code. Try again in a moment.");
      }
    } catch (err) {
      showToast.error("Failed to resend verification code. Please try again.");
      setError("We could not send a new code. Try again in a moment.");
      console.error("Resend verification error:", err);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <OtpView
      variant="email"
      destination={email}
      values={otp}
      onChange={handleChange}
      onSubmit={handleSubmit}
      loading={loading}
      error={error}
      success={success}
      resendSeconds={resendTimer}
      resendLoading={resendLoading}
      onResend={handleResend}
      focusSignal={focusSignal}
    />
  );
}
